import { useEffect, useMemo, useState } from "react";
import { AuthContext, useAuth, type AuthContextProps } from "react-oidc-context";
import type { ReactNode } from "react";

import type { AppConfig } from "../config";

/**
 * Local-only sign-in, for running the whole console offline.
 *
 * ## Why this exists
 *
 * Cognito's hosted UI needs the network and a provisioned pool user, so the
 * real flow cannot run offline. This supplies the one thing the console
 * actually needs from sign-in — an ID token carrying `email` and
 * `cognito:groups`, which is all `useAccess()` reads.
 *
 * ## Why it does not invent a token
 *
 * The token comes from Labeeb's own `POST /dev/admin-token`, which mints one
 * for an email and a group list. That endpoint exists ONLY under
 * `LABEEB_PROFILE=local` and is loopback-guarded, and the admin API behind it
 * verifies what it minted. So the authorities the console renders are the same
 * ones the backend will enforce — a fabricated profile would drift from them
 * silently, and the first real 403 would arrive as a surprise in dev.
 *
 * ## Why it cannot reach a deployed environment
 *
 * Two independent conditions, both required (`devAuthEnabled`): a `devAuth`
 * block in config.json AND `env === "local"`. config.json is written by the
 * deploy from CDK outputs and never contains the block, and `env` comes from
 * the same file — so turning this on in dev or prod takes two deliberate
 * mistakes, not one. `build.test.ts` additionally asserts no deployed artefact
 * can carry it.
 *
 * ## When Labeeb is not running
 *
 * The console signs in OFFLINE instead of refusing: a local, unsigned profile
 * with the email and groups config.json asks for, marked `local:offline`.
 * That is safe because nothing trusts it — MCP's local door never reads the
 * token (the dev proxy names the actor), and Labeeb, the only product that
 * enforces authority, is down, so its panels say unavailable. The banner says
 * "signed in offline". A Labeeb that answers but has no `/dev/admin-token`
 * (wrong profile) still fails loudly: that is a misconfiguration to fix, not
 * an outage to work around.
 *
 * It implements `AuthContextProps` directly rather than wrapping the OIDC
 * provider, so all eight `useAuth()` call sites work untouched and nothing in
 * the real auth path is modified to accommodate it.
 */
export interface DevAuthConfig {
  /** Labeeb's mint endpoint, through the Vite proxy. */
  tokenUrl: string;
  email: string;
  /** Cognito group names — `admin-owner` for full authority. */
  groups: string[];
}

export function devAuthEnabled(config: AppConfig): boolean {
  return config.env === "local" && config.devAuth !== undefined;
}

/** Claims off a JWT payload, with no verification — the backend does that. */
function claimsOf(idToken: string): Record<string, unknown> {
  const payload = idToken.split(".")[1];
  if (!payload) return {};
  try {
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return {};
  }
}

/** The claim that marks an offline profile; read through `useLocalOffline`. */
const OFFLINE_CLAIM = "local:offline";

/**
 * A JWT-shaped, UNSIGNED token for the offline profile. Only this console reads
 * it (for `useAccess`); no backend that verifies tokens is reachable to see it.
 */
function offlineToken(dev: DevAuthConfig): { idToken: string; claims: Record<string, unknown> } {
  const claims = { email: dev.email, "cognito:groups": dev.groups, [OFFLINE_CLAIM]: true };
  const b64 = (o: unknown) =>
    btoa(JSON.stringify(o)).replace(/=+$/, "").replace(/\+/g, "-").replace(/\//g, "_");
  return { idToken: `${b64({ alg: "none", typ: "JWT" })}.${b64(claims)}.`, claims };
}

/** Statuses that mean "nothing is listening behind the proxy", not "Labeeb said no". */
const NOT_RUNNING = new Set([502, 503, 504]);

/** How often an offline sign-in asks Labeeb again. */
const RETRY_OFFLINE_MS = 5000;

/** True when the local console signed in offline because Labeeb was not running. */
export function useLocalOffline(): boolean {
  const profile = useAuth().user?.profile as Record<string, unknown> | undefined;
  return profile?.[OFFLINE_CLAIM] === true;
}

type State =
  | { kind: "loading" }
  | { kind: "ready"; idToken: string; claims: Record<string, unknown> }
  | { kind: "failed"; error: Error };

export function DevAuthProvider({
  config,
  children,
}: {
  config: AppConfig;
  children: ReactNode;
}) {
  const dev = config.devAuth!;
  const [state, setState] = useState<State>({ kind: "loading" });

  useEffect(() => {
    let live = true;
    let retry: ReturnType<typeof setTimeout> | undefined;
    // Offline is temporary: keep asking Labeeb, and switch to its token the
    // moment it answers — no refresh. Labeeb verifies signatures, so until then
    // every Labeeb write would be refused.
    const offline = () => {
      if (!live) return;
      // Same state object on a repeat, so pages do not re-render (and re-fetch)
      // every few seconds while Labeeb stays down.
      setState((prev) =>
        prev.kind === "ready" && prev.claims[OFFLINE_CLAIM] === true
          ? prev
          : { kind: "ready", ...offlineToken(dev) },
      );
      retry = setTimeout(() => void attempt(), RETRY_OFFLINE_MS);
    };
    const attempt = async () => {
      let resp: Response;
      try {
        resp = await fetch(dev.tokenUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: dev.email, groups: dev.groups }),
        });
      } catch {
        // Nothing listening: Labeeb is not running. Sign in offline.
        offline();
        return;
      }
      if (NOT_RUNNING.has(resp.status)) {
        offline();
        return;
      }
      if (!resp.ok) {
        if (live)
          setState({
            kind: "failed",
            error: new Error(
              `${dev.tokenUrl} returned ${resp.status}. A 404 means Labeeb is running ` +
                `without the local profile, so /dev/admin-token is not mounted.`,
            ),
          });
        return;
      }
      const body = (await resp.json()) as { id_token?: string };
      if (!body.id_token) {
        if (live)
          setState({
            kind: "failed",
            error: new Error(`${dev.tokenUrl} returned no id_token.`),
          });
        return;
      }
      if (live)
        setState({ kind: "ready", idToken: body.id_token, claims: claimsOf(body.id_token) });
    };
    void attempt();
    return () => {
      live = false;
      clearTimeout(retry);
    };
  }, [dev.tokenUrl, dev.email, dev.groups.join(",")]);

  const value = useMemo<AuthContextProps>(() => {
    const unsupported = (name: string) => () => {
      throw new Error(`${name} is not available under local dev auth.`);
    };
    const base = {
      settings: {} as never,
      events: {} as never,
      clearStaleState: async () => {},
      removeUser: async () => {},
      signinRedirect: async () => {},
      signinResourceOwnerCredentials: unsupported("signinResourceOwnerCredentials"),
      signinPopup: unsupported("signinPopup"),
      signinSilent: async () => null,
      signoutRedirect: async () => {},
      signoutPopup: unsupported("signoutPopup"),
      signoutSilent: async () => {},
      querySessionStatus: unsupported("querySessionStatus"),
      revokeTokens: async () => {},
      startSilentRenew: () => {},
      stopSilentRenew: () => {},
    } as unknown as AuthContextProps;
    if (state.kind === "loading")
      return { ...base, isLoading: true, isAuthenticated: false, user: undefined, error: undefined };
    if (state.kind === "failed")
      return {
        ...base,
        isLoading: false,
        isAuthenticated: false,
        user: undefined,
        // oidc-client-ts types this as ErrorContext — an Error carrying a
        // `source`. App.tsx only reads `.message`, but the field is typed and
        // a plain Error does not satisfy it.
        error: Object.assign(state.error, { source: "unknown" as const }),
      };
    return {
      ...base,
      isLoading: false,
      isAuthenticated: true,
      error: undefined,
      user: {
        id_token: state.idToken,
        access_token: state.idToken,
        token_type: "Bearer",
        profile: state.claims,
        expires_at: Math.floor(Date.now() / 1000) + 3600,
        expired: false,
        scopes: [],
        state: undefined,
        session_state: null,
        refresh_token: undefined,
        scope: undefined,
      } as unknown as NonNullable<AuthContextProps["user"]>,
    };
  }, [state]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
